import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { Controller, useForm } from 'react-hook-form';
import useTranslationWithFallback from 'hooks/use-translation-with-fallback';

// Context
import { useToastContext } from 'context/toast-context';

// Store
import { useDispatch, useSelector } from 'react-redux';
import { register, selectIsRegistering } from 'store/slices/userSlice';

// Utils
import bemify from 'utils/bemify';
import { EMAIL_PATTERN, PASSWORD_MINIMUM_LENGTH, PASSWORD_PATTERN } from 'utils/form-validation';
import showCapsLockWarning from 'utils/show-caps-lock-message-on-warning-input';
import { supabase } from 'utils/supabase-client';
import { SUPABASE_ERROR_CODES } from 'utils/supabase-errors';

// Components
import Button from 'components/button';
// This component was used for the "Remember Password" input, that is not currently being used
// import Checkbox from 'components/checkbox';
import Icon from 'components/icon';
import Input from 'components/input';
import Loader from 'components/loader';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const RegisterForm = ({ className = null }) => {
  const { t } = useTranslationWithFallback('register');

  const router = useRouter();
  const dispatch = useDispatch();

  // Context
  const { showToast } = useToastContext();

  // Selectors
  const isRegistering = useSelector(selectIsRegistering);

  const [capsLockMessage, setCapsLockMessage] = useState(null);
  const [inputType, setInputType] = useState('password');

  const { handleSubmit, formState: { errors }, control, setError, setFocus } = useForm({
    defaultValues: {
      checkbox: false,
      email: '',
      familyName: '',
      givenName: '',
      password: ''
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  useEffect(() => {
    setFocus('givenName');
  }, [setFocus]);

  const handleInputIconClick = () => {
    if (inputType === 'password') setInputType('text');
    else setInputType('password');

    setFocus('password');
  };

  const handleInputKeyUpDown = (event, name) => {
    const showMessage = showCapsLockWarning(event, name);

    if (showMessage) setCapsLockMessage(t('form.fields.password.caps_lock_message'));
    else setCapsLockMessage(null);
  };

  const handleRegister = async (data) => {
    const {
      error,
      payload
    } = await dispatch(register(data));

    if (error) {
      const { code } = payload;

      if (code === SUPABASE_ERROR_CODES.WEAK_PASSWORD) {
        setError('password', { message: t('form.fields.password.errors.valid') });
        setFocus('password');
      } else if (code === SUPABASE_ERROR_CODES.EMAIL_TAKEN) {
        setError('email', { message: t('form.fields.email.already_taken') });
        setFocus('email');
      }
    } else {
      showToast(t('success_message'), 'success');

      router.push('/login');
    }
  };

  const handleFormSubmit = async (data) => {
    const {
      email,
      givenName,
      familyName,
      password
    } = data;

    handleRegister({
      email,
      familyName,
      givenName,
      password
    });
  };

  const handleGoogleLogin = () => {
    supabase.auth.signInWithOAuth({
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_BASE_URL}`
      },
      provider: 'google'
    });
  };

  // For now, we want to ensure that the password has at least 8 characters only
  // const passwordRequirements = t('form.fields.password.requirements', null, { returnObjects: true });
  const passwordRequirements = [t('form.fields.password.requirements', null, { returnObjects: true })?.[0]];

  const passwordInputIcon = (
    <Button
      disabled={isRegistering}
      icon={inputType === 'password' ? 'eye-closed' : 'eye'}
      noBackground
      paddingSize="none"
      theme="secondary"
      onClick={handleInputIconClick}
    />
  );

  return (
    <div className={`${styles.registerForm__wrapper}${className ? ` ${className}` : ''}${isRegistering ? ` ${styles.registerForm__wrapper_submitting}` : ''}`}>
      <h3 className={styles.registerForm__title}>
        {t('form.title')}
      </h3>
      <form
        className={styles.registerForm}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className={styles.registerForm__sideBySideInput}>
          <Controller
            control={control}
            name="givenName"
            render={({ field: { onChange, onBlur, ref, value } }) => (
              <Input
                ref={ref}
                className={`${styles.registerForm__input} ${styles.registerForm__input_noMarginTop}`}
                description={errors.givenName?.message}
                disabled={isRegistering}
                id="given-name"
                label={t('form.fields.given_name.label')}
                placeholder={t('form.fields.given_name.placeholder')}
                type="text"
                validationState={errors.givenName?.message ? 'error' : null}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
              />
            )}
            rules={{ required: t('form.fields.given_name.error') }}
          />
          <Controller
            control={control}
            name="familyName"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`${styles.registerForm__input} ${styles.registerForm__input_noMarginTop}`}
                description={errors.familyName?.message}
                disabled={isRegistering}
                id="family-name"
                label={t('form.fields.family_name.label')}
                placeholder={t('form.fields.family_name.placeholder')}
                type="text"
                validationState={errors.familyName?.message ? 'error' : null}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
              />
            )}
            rules={{ required: t('form.fields.family_name.error') }}
          />
        </div>
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              autoComplete="email"
              className={styles.registerForm__input}
              description={errors.email?.message}
              disabled={isRegistering}
              id="email"
              label={t('form.fields.email.label')}
              placeholder={t('form.fields.email.placeholder')}
              validationState={errors.email?.message ? 'error' : null}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
          rules={{
            pattern: {
              message: t('form.fields.email.error'),
              value: EMAIL_PATTERN
            },
            required: t('form.fields.email.error')
          }}
        />
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, onBlur, name, ref, value } }) => (
            <Input
              ref={ref}
              autoComplete="password"
              className={styles.registerForm__input}
              description={errors.password?.message}
              disabled={isRegistering}
              id="password"
              label={t('form.fields.password.label')}
              placeholder={t('form.fields.password.placeholder')}
              rightSlot={passwordInputIcon}
              type={inputType}
              validationState={errors.password?.message ? 'error' : null}
              value={value}
              warningMessage={capsLockMessage}
              onBlur={onBlur}
              onChange={onChange}
              onKeyDown={event => handleInputKeyUpDown(event, name)}
              onKeyUp={event => handleInputKeyUpDown(event, name)}
            />
          )}
          rules={{
            minLength: {
              message: t('form.fields.password.errors.valid'),
              value: PASSWORD_MINIMUM_LENGTH
            },
            pattern: {
              message: t('form.fields.password.errors.valid'),
              value: PASSWORD_PATTERN
            },
            required: t('form.fields.password.errors.required')
          }}
        />
        {Array.isArray(passwordRequirements) && !!passwordRequirements.length && (
          <div className={styles.registerForm__passwordRequirementsWrapper}>
            {passwordRequirements.map((requirement, index) => (
              <div
                key={index}
                className={styles.registerForm__passwordRequirement}
              >
                <Icon
                  className={styles.registerForm__passwordRequirementIcon}
                  name="check-circle"
                />
                <span>
                  {requirement}
                </span>
              </div>
            ))}
          </div>
        )}
        {/* This is not doing anything at the moment, so it is commented */}
        {/* <Controller
          control={control}
          name="checkbox"
          render={({ field: { onChange, onBlur, value } }) => (
            <Checkbox
              checked={value}
              className={styles.registerForm__checkbox}
              disabled={isRegistering}
              id="remember-password-checkbox"
              label={t('form.fields.checkbox.label')}
              onBlur={onBlur}
              onCheckedChange={onChange}
            />
          )}
        /> */}
        <div className={styles.registerForm__loaderAndSubmitButton}>
          <div className={`${styles.registerForm__loaderWrapper}${isRegistering ? ` ${styles.registerForm__loaderWrapper_visible}` : ''}`}>
            <div className={styles.registerForm__loader}>
              <Loader
                color="white"
                direction="anti-clockwise"
                icon="loader"
              />
            </div>
          </div>
          <Button
            className={`${styles.registerForm__submitButton}${isRegistering ? ` ${styles.registerForm__submitButton_hidden}` : ''}`}
            text={t('form.submit_button')}
            type="submit"
          />
        </div>
      </form>
      <div className={`${styles.registerForm__registerAlternatives}${isRegistering ? ` ${styles.registerForm__registerAlternatives_hidden}` : ''}`}>
        <Button
          className={styles.registerForm__forgotPasswordLink}
          href="/request-password-reset"
          noBackground
          paddingSize="none"
          text={t('forgot_password.link.label')}
          textWeight="regular"
          theme="secondary"
          underlineOnHover
        />
        <div className={styles.registerForm__divider}>
          <hr className={styles.registerForm__dividerLine} />
          <span className={styles.registerForm__dividerText}>
            {t('or')}
          </span>
          <hr className={styles.registerForm__dividerLine} />
        </div>
        <Button
          className={styles.registerForm__googleButton}
          icon="google"
          iconPosition="left"
          text={t('google_button')}
          textWeight="regular"
          theme="google"
          onClick={handleGoogleLogin}
        />
        <div className={styles.registerForm__createAccountText}>
          <span>
            {t('login.text')}
          </span>
          <Button
            className={styles.registerForm__createAccountLink}
            href="/login"
            noBackground
            paddingSize="none"
            text={t('login.link.label')}
            textWeight="regular"
            theme="primary"
            underlineOnHover
          />
        </div>
      </div>
    </div>
  );
};

RegisterForm.propTypes = {
  className: PropTypes.string
};

export default RegisterForm;
